import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { WifiOff, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useOutbox } from './useOutbox';
import { surfaceQueuesMessages } from './offlineSurfaces';

/**
 * The thin bar at the top of the app when the network is gone or a message is
 * still waiting.
 *
 * It is mounted once, above the router, so whatever it says it says to every
 * screen. It used to tell all of them "messages you send now queue locally and
 * go out when you reconnect" — true on Shop Chat, false everywhere else, and
 * most damagingly false on Diagnose, where a message sent offline is lost
 * without a word. A promise the product can't keep costs more trust than a
 * missing feature, so the promise now follows the one surface that keeps it.
 */
export function OfflineBanner() {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const { online, pendingCount, stuck, draining, storageError, discardStuck } = useOutbox();

  if (storageError) {
    return (
      <Bar tone="danger">
        <AlertTriangle size={13} strokeWidth={2} aria-hidden />
        <span>{t('chrome.offline.storageError')}</span>
      </Bar>
    );
  }

  if (online && pendingCount === 0 && stuck.length === 0) return null;

  const status = online
    ? t(draining ? 'chrome.offline.sending' : 'chrome.offline.waitingToSend', {
        count: pendingCount,
      })
    : surfaceQueuesMessages(pathname)
      ? t(pendingCount > 0 ? 'chrome.offline.heldWaiting' : 'chrome.offline.held', {
          waiting: pendingCount,
        })
      : t(pendingCount > 0 ? 'chrome.offline.nothingWaiting' : 'chrome.offline.nothing', {
          waiting: pendingCount,
        });

  return (
    <Bar tone={stuck.length > 0 ? 'danger' : 'warning'}>
      {/* The offline state is never swallowed by the stuck state — a tech who
          is BOTH offline and holding a dead message needs to know both, or the
          re-type they're being told to do vanishes the same way. */}
      {(pendingCount > 0 || !online) && (
        <span className="flex items-center gap-1.5">
          <WifiOff size={13} strokeWidth={2} aria-hidden />
          {status}
        </span>
      )}
      {stuck[0] && (
        <span className="flex items-center gap-1.5">
          <AlertTriangle size={13} strokeWidth={2} aria-hidden />
          {/* The tech's own words. "Say it again" is useless advice if they
              can't read what "it" was, and deleting is the only other option
              on offer. */}
          <span>
            {t(stuck.length > 1 ? 'chrome.offline.stuckMore' : 'chrome.offline.stuckOne', {
              preview: truncate(stuck[0].preview, t('chrome.offline.yourMessage')),
              more: stuck.length - 1,
            })}
          </span>
          <button
            type="button"
            onClick={() => void discardStuck()}
            className="hover:bg-fw-danger/15 focus-visible:ring-fw-danger -my-1.5 inline-flex min-h-11 items-center rounded px-1.5 font-semibold underline underline-offset-2 transition-colors duration-150 focus-visible:ring-2 focus-visible:outline-none"
          >
            {t('chrome.offline.discard', { count: stuck.length })}
          </button>
        </span>
      )}
    </Bar>
  );
}

function truncate(text: string, fallback: string): string {
  const clean = text.trim();
  if (!clean) return fallback;
  return clean.length > 44 ? `${clean.slice(0, 44)}…` : clean;
}

function Bar({ tone, children }: { tone: 'warning' | 'danger'; children: React.ReactNode }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'sticky top-0 z-50 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 border-b px-4 py-1.5 text-[12px] font-medium',
        tone === 'danger'
          ? 'border-fw-danger/40 bg-fw-danger-soft text-fw-danger'
          : 'border-fw-warning/40 bg-fw-warning-soft text-fw-warning',
      )}
    >
      {children}
    </div>
  );
}
